import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { UserRole } from '@/constants/roles';

export type AuthUser = {
  id: string;
  email: string;
  displayName: string;
  tenantId?: string | null;
};

export type AuthState = {
  user: AuthUser | null;
  roles: UserRole[];
  refreshCredential: string | null;
  accessToken: string | null;
  preferredLocale: string | null;
};

const initialState: AuthState = {
  user: null,
  roles: [],
  refreshCredential: null,
  accessToken: null,
  preferredLocale: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthenticatedUser(
      state,
      action: PayloadAction<{
        user: AuthUser;
        roles: UserRole[];
        refreshCredential?: string | null;
        accessToken?: string | null;
      }>,
    ) {
      state.user = action.payload.user;
      state.roles = action.payload.roles;
      state.refreshCredential = action.payload.refreshCredential ?? null;
      state.accessToken = action.payload.accessToken ?? null;
    },
    clearAuthenticatedUser(state) {
      state.user = null;
      state.roles = [];
      state.refreshCredential = null;
      state.accessToken = null;
      state.preferredLocale = null;
    },
    updateBreakGlassTokens(
      state,
      action: PayloadAction<{ accessToken: string; refreshCredential: string }>,
    ) {
      state.accessToken = action.payload.accessToken;
      state.refreshCredential = action.payload.refreshCredential;
    },
    setPreferredLocale(state, action: PayloadAction<string | null>) {
      state.preferredLocale = action.payload;
    },
  },
});

export const { setAuthenticatedUser, clearAuthenticatedUser, updateBreakGlassTokens, setPreferredLocale } =
  authSlice.actions;

export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectRoles = (state: { auth: AuthState }) => state.auth.roles;
export const selectRefreshCredential = (state: { auth: AuthState }) => state.auth.refreshCredential;
export const selectAccessToken = (state: { auth: AuthState }) => state.auth.accessToken;
export const selectPreferredLocale = (state: { auth: AuthState }) => state.auth.preferredLocale;
